import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Mail, MapPin, Phone, CheckCircle, XCircle } from 'lucide-react'
import { useForm, ValidationError } from '@formspree/react'

export function ContactSection() {
  const [state, handleSubmit] = useForm("mrbkzobk")
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [showError, setShowError] = useState(false)

  // Form reset 
  useEffect(() => {
    if (state.succeeded) {
      setIsSubmitted(true)
      // 5 seconds success message hide 
      const timer = setTimeout(() => {
        setIsSubmitted(false)
      }, 5000)
      return () => clearTimeout(timer)
    }
    
    // Error  show 
    if (state.errors) {
      setShowError(true)
      const timer = setTimeout(() => {
        setShowError(false)
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [state.succeeded, state.errors])

  return (
    <section id="contact" className="py-24 bg-ocean-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Info  */}
          <div>
            <h2 className="text-ocean-500 font-semibold tracking-wider uppercase text-sm mb-3">
              Get in Touch
            </h2>
            <h3 className="text-3xl md:text-4xl font-bold text-navy-900 mb-6">
              Partner with ENERSIC
            </h3>
            <p className="text-lg text-gray-600 mb-8">
              Whether you're an investor, technology provider, or community
              representative, we'd love to discuss how we can work together to
              power the future.
            </p>

            <div className="space-y-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <Mail className="h-6 w-6 text-ocean-900" />
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-medium text-navy-900">
                    Email Us
                  </h4>
                  <p className="text-gray-600">info@enersic.com</p>
                  <p className="text-gray-600">partnerships@enersic.com</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <MapPin className="h-6 w-6 text-ocean-900" />
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-medium text-navy-900">
                    Visit Us
                  </h4>
                  <p className="text-gray-600">
                    Flat B3, 32/20 Kha, Block: C  
                    <br />
                    Tajmahal Road, Dhaka-1207 
                    <br />
                    Bangladesh
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <Phone className="h-6 w-6 text-ocean-900" />
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-medium text-navy-900">Call Us</h4>
                  <p className="text-gray-600">+880-1773079243</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <motion.div
            initial={{
              opacity: 0,
              y: 20,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
            }}
            className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100"
          >
            {/* Success Message */}
            {isSubmitted && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg"
              >
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  <p className="text-green-700 font-medium">
                    Message sent successfully! We'll get back to you soon.
                  </p>
                </div>
              </motion.div>
            )}

            {/* Error Message - FIXED */}
            {showError && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
              >
                <div className="flex items-center">
                  <XCircle className="h-5 w-5 text-red-500 mr-2" />
                  <div>
                    <p className="text-red-700 font-medium mb-1">
                      There was an error sending your message.
                    </p>
                    <p className="text-red-600 text-sm">
                      Please check all required fields and try again.
                    </p>
                  </div>
                </div>
              </motion.div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="firstName"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent outline-none transition-all"
                    placeholder="Jane"
                    required
                  />
                  <ValidationError 
                    prefix="First Name" 
                    field="firstName"
                    errors={state.errors}
                    className="text-red-500 text-sm mt-1"
                  />
                </div>
                <div>
                  <label
                    htmlFor="lastName"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent outline-none transition-all"
                    placeholder="Doe"
                  />
                  <ValidationError 
                    prefix="Last Name" 
                    field="lastName"
                    errors={state.errors}
                    className="text-red-500 text-sm mt-1"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent outline-none transition-all"
                  placeholder="jane@company.com"
                  required
                />
                <ValidationError 
                  prefix="Email" 
                  field="email"
                  errors={state.errors}
                  className="text-red-500 text-sm mt-1"
                />
              </div>

              <div>
                <label
                  htmlFor="subject"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Subject
                </label>
                <select
                  id="subject"
                  name="subject"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent outline-none transition-all bg-white"
                  defaultValue="Partnership Inquiry"
                >
                  <option value="Partnership Inquiry">Partnership Inquiry</option>
                  <option value="Investor Relations">Investor Relations</option>
                  <option value="General Information">General Information</option>
                  <option value="Press & Media">Press & Media</option>
                </select>
                <ValidationError 
                  prefix="Subject" 
                  field="subject"
                  errors={state.errors}
                  className="text-red-500 text-sm mt-1"
                />
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent outline-none transition-all resize-none"
                  placeholder="How can we help you?"
                  required
                ></textarea>
                <ValidationError 
                  prefix="Message" 
                  field="message"
                  errors={state.errors}
                  className="text-red-500 text-sm mt-1"
                />
              </div>

              <button
                type="submit"
                disabled={state.submitting || isSubmitted}
                className="w-full py-3 px-6 bg-ocean-900 text-white font-bold rounded-lg hover:bg-ocean-800 transition-colors shadow-md hover:shadow-lg transform hover:-translate-y-0.5 duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {state.submitting ? 'Sending...' : 
                 isSubmitted ? 'Sent Successfully!' : 
                 'Send Message'}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  )
}